import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  TrendingUp,
  CheckCircle,
  Lightbulb,
  Warning,
  Info,
  Timeline,
  Agriculture,
  Speed,
} from '@mui/icons-material';
import SupportShell from './SupportShell';

const YieldOptimizationPage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState<string | false>('growth-monitoring');

  const handleBackToSupport = () => {
    navigate('/support');
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const optimizationMetrics = [
    { metric: 'Yield per m²', target: '15-25 kg', current: '18.5 kg', progress: 74 },
    { metric: 'Growth Rate', target: '2-3 cm/day', current: '2.4 cm/day', progress: 80 },
    { metric: 'Nutrient Efficiency', target: '85-95%', current: '89%', progress: 89 },
    { metric: 'Water Use Efficiency', target: '3-5 L/kg', current: '3.8 L/kg', progress: 82 },
  ];

  const sections = [
    {
      id: 'growth-monitoring',
      title: 'Growth Monitoring & Data Analysis',
      icon: <Timeline color="primary" />,
      content: {
        overview: 'Systematic monitoring and analysis of plant growth parameters for optimization.',
        keyPoints: [
          'Key performance indicators (KPIs) for hydroponic systems',
          'Data collection protocols and frequency',
          'Statistical analysis and trend identification',
          'Predictive modeling for yield forecasting',
        ],
        details: `Effective yield optimization requires systematic data collection and analysis. Monitor plant height, leaf area, stem diameter, and biomass accumulation. Track environmental parameters including temperature, humidity, light intensity, and photoperiod.

Use statistical process control to identify trends and deviations. Calculate growth rates, nutrient uptake efficiency, and water use efficiency. Implement predictive models to forecast yields and identify optimization opportunities.

Key metrics include:
- Daily growth rate (cm/day)
- Leaf area index (LAI)
- Dry matter accumulation
- Harvest index (economic yield/total biomass)`,
        tips: [
          'Establish baseline measurements for comparison',
          'Use consistent measurement protocols and timing',
          'Implement automated monitoring where possible',
          'Correlate growth data with environmental conditions',
        ],
      },
    },
    {
      id: 'nutrient-scheduling',
      title: 'Nutrient Scheduling & Stage Management',
      icon: <Agriculture color="primary" />,
      content: {
        overview: 'Optimizing nutrient delivery timing and concentrations for maximum yield.',
        keyPoints: [
          'Growth stage-specific nutrient requirements',
          'Dynamic EC and pH management',
          'Nutrient timing and frequency optimization',
          'Stress-induced quality enhancement',
        ],
        details: `Nutrient scheduling involves adjusting formulations based on plant growth stages and physiological needs. Seedlings require lower EC (0.8-1.2 mS/cm) with higher N:K ratios. Vegetative growth benefits from increased nitrogen and moderate EC (1.4-1.8 mS/cm).

Flowering and fruiting stages require reduced nitrogen, increased phosphorus and potassium, and higher EC (1.8-2.4 mS/cm). Implement controlled stress techniques like EC manipulation or nutrient restriction to enhance quality parameters.

Advanced techniques include:
- Circadian nutrient cycling
- Deficit irrigation strategies
- Pre-harvest nutrient manipulation
- Quality-focused finishing protocols`,
        tips: [
          'Monitor plant response to schedule changes',
          'Adjust timing based on environmental conditions',
          'Use tissue analysis to validate nutrient status',
          'Document successful protocols for replication',
        ],
      },
    },
    {
      id: 'environmental-control',
      title: 'Environmental Control Integration',
      icon: <Speed color="primary" />,
      content: {
        overview: 'Coordinating environmental controls with nutrition for optimal plant performance.',
        keyPoints: [
          'Temperature and nutrient uptake relationships',
          'Light intensity effects on nutrient demand',
          'Humidity and transpiration management',
          'CO₂ supplementation and nutrition synergy',
        ],
        details: `Environmental factors significantly impact nutrient uptake and utilization. Higher temperatures increase metabolic rates and nutrient demand but can reduce nutrient solution oxygen levels. Light intensity directly affects photosynthetic rate and nutrient requirements.

Vapor pressure deficit (VPD) management affects transpiration rates and nutrient uptake. Optimal VPD ranges (0.8-1.2 kPa) promote efficient nutrient transport. CO₂ supplementation increases photosynthetic capacity, requiring adjusted nutrient levels to support enhanced growth.

Integration strategies:
- Temperature-compensated EC adjustments
- Light-responsive nutrient scheduling
- VPD-based irrigation timing
- CO₂-synchronized nutrient delivery`,
        tips: [
          'Monitor environmental-nutrient interactions',
          'Adjust nutrient levels with environmental changes',
          'Use integrated control systems for coordination',
          'Validate changes with plant response monitoring',
        ],
      },
    },
    {
      id: 'harvest-timing',
      title: 'Harvest Timing & Quality Optimization',
      icon: <TrendingUp color="primary" />,
      content: {
        overview: 'Optimizing harvest timing and pre-harvest treatments for maximum quality and yield.',
        keyPoints: [
          'Maturity indicators and harvest windows',
          'Pre-harvest nutrient manipulation',
          'Quality parameter optimization',
          'Post-harvest handling considerations',
        ],
        details: `Harvest timing significantly impacts both yield and quality. Monitor maturity indicators including sugar content (Brix), firmness, color development, and size. Use destructive sampling to establish harvest criteria for each variety.

Pre-harvest treatments can enhance quality:
- Reduced nitrogen 7-14 days before harvest
- Increased potassium for improved shelf life
- Controlled water stress for concentrated flavors
- Strategic lighting for color development

Quality optimization techniques:
- Sugar accumulation protocols
- Antioxidant enhancement strategies
- Texture and firmness optimization
- Shelf life extension methods`,
        tips: [
          'Establish variety-specific harvest criteria',
          'Monitor quality parameters throughout development',
          'Test pre-harvest treatments on small batches',
          'Coordinate harvest timing with market demands',
        ],
      },
    },
  ];

    return (
    <SupportShell
      title="Yield optimization"
      subtitle="Operational practices around nutrient recipes and crop performance."
    >

          {/* Introduction Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Maximizing Hydroponic Performance
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                Yield optimization in hydroponic systems requires a systematic approach combining precise nutrition, 
                environmental control, and data-driven decision making. Professional growers achieve 20-30% higher 
                yields compared to traditional methods through optimized management protocols.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                This expert-level guide covers advanced optimization strategies used by commercial hydroponic 
                operations to maximize both yield and quality while maintaining economic efficiency.
              </Typography>
            </CardContent>
          </Card>

          {/* Performance Metrics */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1.5 }}>
              Key Performance Indicators
            </Typography>
            <Grid container spacing={3}>
              {optimizationMetrics.map((metric, index) => (
                <Grid key={index} size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h4" sx={{ fontWeight: 600 }}>
                          {metric.metric}
                        </Typography>
                        <Chip 
                          label={`${metric.progress}%`} 
                          color={metric.progress >= 80 ? 'success' : metric.progress >= 60 ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Target: {metric.target} | Current: {metric.current}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={metric.progress} 
                        sx={{ height: 8, borderRadius: 4 }}
                        color={metric.progress >= 80 ? 'success' : metric.progress >= 60 ? 'warning' : 'error'}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Learning Objectives */}
          <Paper sx={{ p: 4, mb: 4, bgcolor: 'error.50' }}>
            <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1.5 }}>
              Expert Learning Objectives
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Implement systematic growth monitoring and data analysis protocols" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Develop stage-specific nutrient scheduling strategies" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Integrate environmental controls with nutrition management" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Optimize harvest timing and quality enhancement techniques" />
              </ListItem>
            </List>
          </Paper>

          {/* Content Sections */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 4 }}>
            Expert Topics
          </Typography>

          {sections.map((section) => (
            <Accordion
              key={section.id}
              expanded={expandedSection === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ 
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center',
                    gap: 2 
                  }
                }}
              >
                {section.icon}
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  {section.content.overview}
                </Typography>

                <Typography variant="h6" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Advanced Concepts
                </Typography>
                <List sx={{ mb: 3 }}>
                  {section.content.keyPoints.map((point, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>

                <Typography variant="h6" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Professional Implementation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  {section.content.details}
                </Typography>

                <Paper sx={{ p: 3, bgcolor: 'warning.50' }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Lightbulb color="warning" />
                    <Box>
                      <Typography variant="h6" component="h5" sx={{ fontWeight: 600, mb: 2 }}>
                        Expert Strategies
                      </Typography>
                      <List>
                        {section.content.tips.map((tip, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Info color="info" sx={{ fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText primary={tip} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Stack>
                </Paper>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Success Factors */}
          <Card sx={{ mt: 6, mb: 4 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1.5 }}>
                Critical Success Factors
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper sx={{ p: 3, textAlign: 'left', bgcolor: 'success.50' }}>
                    <Timeline sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Data-Driven Decisions
                    </Typography>
                    <Typography variant="body2">
                      Systematic monitoring and analysis of all growth parameters
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper sx={{ p: 3, textAlign: 'left', bgcolor: 'info.50' }}>
                    <Agriculture sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Precision Nutrition
                    </Typography>
                    <Typography variant="body2">
                      Stage-specific formulations with environmental integration
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper sx={{ p: 3, textAlign: 'left', bgcolor: 'warning.50' }}>
                    <Speed sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Continuous Optimization
                    </Typography>
                    <Typography variant="body2">
                      Regular protocol refinement based on performance feedback
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent sx={{ p: 2, textAlign: 'left' }}>
              <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1.5 }}>
                Ready to Optimize Your Yields?
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7 }}>
                Apply these expert optimization strategies using FlahaFast's professional calculation and 
                monitoring tools to achieve maximum productivity in your hydroponic operation.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => navigate('/support')}
                  sx={{ borderRadius: 2 }}
                >
                  All guides
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{ borderRadius: 2 }}
                >
                  Open app
                </Button>
              </Stack>
            </CardContent>
          </Card>
    </SupportShell>
  );
};

export default YieldOptimizationPage;
